// invest_FE/src/services/adminService.js
import { apiFetch } from '../utils/apiClient';
import { BASE_URL } from '../utils/config';

/**
 * Fetches system events from the backend with filtering, sorting, and pagination.
 * @param {object} params - An object containing query parameters.
 * @param {number} [params.page] - The page number for pagination.
 * @param {number} [params.limit] - The number of items per page.
 * @param {string} [params.sortBy] - The field to sort by.
 * @param {string} [params.sortOrder] - The sort order ('asc' or 'desc').
 * @param {string} [params.eventType] - Filter by event type.
 * @param {string} [params.severity] - Filter by severity (comma-separated).
 * @param {string} [params.user] - Filter by user ID.
 * @param {string} [params.ipAddress] - Filter by IP address.
 * @param {string} [params.startDate] - Filter by start date (ISO string).
 * @param {string} [params.endDate] - Filter by end date (ISO string).
 * @param {string} token - The user's authentication token.
 * @returns {Promise<object>} The response data from the API, which includes { events, currentPage, totalPages, totalEvents }.
 */
export const getEvents = async (params, token) => {
  try {
    // Construct query string from params object
    const query = new URLSearchParams(params).toString();
    const url = `${BASE_URL}/api/events?${query}`;

    const response = await apiFetch(url, { method: 'GET' }, token);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || 'Failed to fetch events');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching events:', error);
    // Re-throw the error so the component can catch it.
    throw new Error(error.message || 'An unknown error occurred while fetching events.');
  }
};

/**
 * Triggers a specific backend admin task.
 * @param {{propertyId: string, taskName: string, params?: object}} data - The task data.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The response from the server.
 */
export const triggerAdminTask = async (data, token) => {
  const response = await apiFetch(
    `${BASE_URL}/api/admin/trigger-task`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
    token
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to trigger admin task' }));
    throw new Error(errorData.message);
  }

  return response.json();
};

/**
 * Creates a new admin email draft.
 * @param {object} emailData - The email data (subject, messages, filesChanged).
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The created email draft object.
 */
export const createAdminEmail = async (emailData, token) => {
  const response = await apiFetch(
    `${BASE_URL}/api/admin/admin-emails`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(emailData),
    },
    token
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to create email draft' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Updates an existing admin email draft.
 * @param {string} emailId - The ID of the email draft to update.
 * @param {object} emailData - The updated email data.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The updated email draft object.
 */
export const updateAdminEmail = async (emailId, emailData, token) => {
  const response = await apiFetch(
    `${BASE_URL}/api/admin/admin-emails/${emailId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(emailData),
    },
    token
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to update email draft.' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Deletes an admin email draft.
 * @param {string} emailId - The ID of the email draft to delete.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The success message from the server.
 */
export const deleteAdminEmail = async (emailId, token) => {
  const response = await apiFetch(
    `${BASE_URL}/api/admin/admin-emails/${emailId}`,
    {
      method: 'DELETE',
    },
    token
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to delete email draft.' }));
    throw new Error(errorData.message);
  }
  return response.json();
};
/**
 * Checks if the application is authenticated with Zoho Campaigns.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<{isAuthenticated: boolean}>} An object containing the authentication status.
 */
export const getZohoStatus = async (token) => {
  const response = await apiFetch(`${BASE_URL}/api/admin/zoho-status`, {}, token);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to get Zoho status' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Fetches all admin emails (drafts and sent).
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<Array<object>>} A list of all admin emails.
 */
export const getAdminEmails = async (token) => {
  const response = await apiFetch(`${BASE_URL}/api/admin/admin-emails`, {}, token);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to fetch admin emails' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Sends a specific admin email draft.
 * @param {object} payload - The request payload. Can be { id: 'emailId' } or { onlyCreateLists: true }.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The success message from the server.
 */
export const sendAdminEmail = async (payload, token) => {
  const response = await apiFetch(
    `${BASE_URL}/api/admin/admin-emails/send`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    token
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to send email' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Fetches the Zoho OAuth authorization URL from the backend.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<{authUrl: string}>} An object containing the authorization URL.
 */
export const getZohoAuthUrl = async (token) => {
  const response = await apiFetch(`${BASE_URL}/api/admin/zoho-auth-url`, {}, token);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to get Zoho auth URL' }));
    throw new Error(errorData.message);
  }
  return response.json();
};
